import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  Box, Typography, Button, Checkbox, IconButton, Tooltip,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  TextField, Chip, Collapse,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TuneIcon from '@material-ui/icons/Tune';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SendIcon from '@material-ui/icons/Send';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PersonIcon from '@material-ui/icons/Person';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import {
  optimizeVRP, dispatchVrpPlan, clearCurrentPlan,
  selectVrpCurrentPlan, selectVrpOptimizing, selectVrpDispatching, selectVrpError,
} from '../../store/slices/vrpSlice';
import { AuthContext } from '../../App';

const ROUTE_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#f97316', '#14b8a6', '#6366f1',
];

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
    overflow: 'hidden',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    height: 56,
    flexShrink: 0,
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  statsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 24px 10px',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  statChip: {
    fontWeight: 700,
    fontSize: '0.72rem',
    height: 26,
    background: '#e2e8f0',
    color: '#475569',
  },
  body: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  leftPanel: {
    width: 340,
    flexShrink: 0,
    borderRight: '1px solid #e2e8f0',
    overflowY: 'auto',
    background: '#f1f5f9',
    padding: '10px 10px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sectionCard: {
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { background: '#f8fafc' },
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionIcon: {
    width: 28, height: 28, borderRadius: 7,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#1e293b',
  },
  sectionCount: {
    fontSize: '0.56rem',
    fontWeight: 700,
    borderRadius: 8,
    padding: '1px 7px',
    lineHeight: 1.6,
  },
  sectionBody: {
    borderTop: '1px solid #f1f5f9',
  },
  listWrap: {
    maxHeight: 160,
    overflowY: 'auto',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px 5px 10px',
    fontSize: '0.7rem',
    color: '#1e293b',
    borderBottom: '1px solid #fafbfc',
    cursor: 'pointer',
    transition: 'background 0.1s',
    '&:hover': { background: '#eef2ff' },
  },
  listItemActive: {
    background: '#eef2ff',
  },
  listItemSub: {
    fontSize: '0.56rem',
    color: '#94a3b8',
    lineHeight: 1.2,
  },
  configSection: {
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  configField: {
    '& .MuiOutlinedInput-root': { borderRadius: 7, fontSize: '0.7rem', background: '#fafbfc', height: 32 },
    '& .MuiOutlinedInput-input': { padding: '6px 10px' },
    '& .MuiInputLabel-outlined': { fontSize: '0.66rem' },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrunk': { fontSize: '0.66rem' },
    '& .MuiSelect-outlined': { padding: '6px 10px' },
    '& .MuiSelect-select': { fontSize: '0.7rem' },
  },
  configLabel: {
    fontSize: '0.52rem',
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 2,
  },
  selectAllBtn: {
    fontSize: '0.58rem',
    color: '#3b82f6',
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' },
  },
  centerArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    padding: 24,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    color: '#94a3b8',
  },
  summaryCards: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 24,
    justifyContent: 'center',
  },
  summaryCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '18px 28px',
    minWidth: 150,
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #f1f5f9',
    transition: 'all 0.15s',
    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
  },
  summaryValue: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  summaryLabel: {
    fontSize: '0.62rem',
    color: '#94a3b8',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: 4,
  },
  rightPanel: {
    width: 360,
    flexShrink: 0,
    borderLeft: '1px solid #e2e8f0',
    overflowY: 'auto',
    background: '#fafbfc',
    padding: '0 0 16px',
  },
  routeCard: {
    margin: '8px 12px',
    borderRadius: 12,
    border: '2px solid',
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    transition: 'all 0.15s',
    '&:hover': { boxShadow: '0 3px 12px rgba(0,0,0,0.08)' },
  },
  routeCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { background: '#f8fafc' },
  },
  routeCardInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  routeCardMeta: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    padding: '0 12px 8px',
  },
  routeCardMetaChip: {
    fontSize: '0.65rem',
    height: 20,
    fontWeight: 600,
  },
  stopItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 12px 4px 20px',
    fontSize: '0.72rem',
    color: '#475569',
  },
  stopNumber: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.62rem',
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  },
  unassignedSection: {
    margin: '12px 10px',
    borderRadius: 10,
    border: '2px solid #fecaca',
    background: '#fef2f2',
    overflow: 'hidden',
  },
  unassignedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    color: '#dc2626',
    fontWeight: 700,
    fontSize: '0.78rem',
  },
  unassignedItem: {
    padding: '4px 12px 4px 20px',
    fontSize: '0.72rem',
    color: '#991b1b',
    borderTop: '1px solid #fecaca',
  },
  utilizationBar: {
    height: 6,
    borderRadius: 3,
    background: '#e2e8f0',
    overflow: 'hidden',
    margin: '4px 12px 8px',
  },
  utilizationFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s ease',
  },
  optimizeBtn: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.78rem',
    textTransform: 'none',
    borderRadius: 8,
    padding: '5px 16px',
    '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
    '&.Mui-disabled': { background: '#94a3b8', color: '#fff' },
  },
  dispatchBtn: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.78rem',
    textTransform: 'none',
    borderRadius: 8,
    padding: '5px 16px',
    '&:hover': { background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' },
    '&.Mui-disabled': { background: '#94a3b8', color: '#fff' },
  },
  configField: {
    '& .MuiInputBase-root': { height: 34, fontSize: '0.78rem' },
    margin: '4px 16px',
  },
  selectAll: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 10px',
    fontSize: '0.68rem',
    color: '#3b82f6',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
}));

function VRPView({ lang, embedded }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, userPermission } = useContext(AuthContext);
  const canWrite = userPermission !== 'viewer';

  const currentPlan = useSelector(selectVrpCurrentPlan);
  const optimizing = useSelector(selectVrpOptimizing);
  const dispatching = useSelector(selectVrpDispatching);
  const vrpError = useSelector(selectVrpError);

  // Local state for selections and data
  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [depotId, setDepotId] = useState('');
  const [openSections, setOpenSections] = useState({ orders: true, vehicles: true, drivers: false, depot: true, config: false });
  const toggleSection = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));

  // Configuration
  const [maxStops, setMaxStops] = useState(10);
  const [maxTime, setMaxTime] = useState(600);
  const [optimizeBy, setOptimizeBy] = useState('distance');

  // Route card expansion
  const [expandedRoutes, setExpandedRoutes] = useState({});

  // Fetch data on mount
  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoadingData(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [ordersRes, vehiclesRes, driversRes, locationsRes] = await Promise.all([
        fetch(`${'/planner-api'}/transport-orders?status=pending&limit=200`, { headers }),
        fetch(`${'/planner-api'}/vehicles`, { headers }),
        fetch(`${'/planner-api'}/drivers`, { headers }),
        fetch(`${'/planner-api'}/locations?type=warehouse`, { headers }),
      ]);
      const ordersData = await ordersRes.json().catch(() => []);
      const vehiclesData = await vehiclesRes.json().catch(() => []);
      const driversData = await driversRes.json().catch(() => []);
      const locationsData = await locationsRes.json().catch(() => []);
      setOrders(Array.isArray(ordersData) ? ordersData : ordersData.data || []);
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : vehiclesData.data || []);
      setDrivers(Array.isArray(driversData) ? driversData : driversData.data || []);
      setWarehouses(Array.isArray(locationsData) ? locationsData : locationsData.data || []);
    } catch (e) {
      console.error('VRP fetch error:', e);
    }
    setLoadingData(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-select depot if only one warehouse
  useEffect(() => {
    if (warehouses.length === 1 && !depotId) {
      setDepotId(warehouses[0].id || warehouses[0]._id || '');
    }
  }, [warehouses, depotId]);

  function toggleOrder(id) {
    setSelectedOrders(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleAllOrders() {
    if (selectedOrders.length === orders.length) setSelectedOrders([]);
    else setSelectedOrders(orders.map(o => o.id || o._id));
  }
  function toggleVehicle(id) {
    setSelectedVehicles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleAllVehicles() {
    if (selectedVehicles.length === vehicles.length) setSelectedVehicles([]);
    else setSelectedVehicles(vehicles.map(v => v.id || v._id));
  }
  function toggleDriver(id) {
    setSelectedDrivers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleAllDrivers() {
    if (selectedDrivers.length === drivers.length) setSelectedDrivers([]);
    else setSelectedDrivers(drivers.map(d => d.id || d._id));
  }

  function toggleRouteExpand(idx) {
    setExpandedRoutes(prev => ({ ...prev, [idx]: !prev[idx] }));
  }

  function handleOptimize() {
    if (selectedOrders.length === 0 || selectedVehicles.length === 0) return;
    dispatch(optimizeVRP({
      token,
      params: {
        orderIds: selectedOrders,
        vehicleIds: selectedVehicles,
        driverIds: selectedDrivers,
        depotId: depotId || undefined,
        maxStopsPerRoute: maxStops,
        maxRouteTimeMin: maxTime,
        optimizeBy,
      },
    }));
  }

  function handleDispatch() {
    if (!currentPlan?.id) return;
    dispatch(dispatchVrpPlan({ token, planId: currentPlan.id }));
  }

  // Compute stats from current plan
  const routes = currentPlan?.routes || [];
  const unassigned = currentPlan?.unassigned || [];
  const totalDeliveries = routes.reduce((s, r) => s + (r.stops?.length || 0), 0);
  const totalDistanceKm = routes.reduce((s, r) => s + (r.distanceKm || r.distance || 0), 0);
  const avgUtilization = routes.length > 0
    ? Math.round(routes.reduce((s, r) => s + (r.utilization || r.utilizationPct || 0), 0) / routes.length)
    : 0;

  const isPt = lang === 'pt';

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.titleRow}>
          {!embedded && (
            <IconButton size="small" onClick={() => { dispatch(clearCurrentPlan()); dispatch(setActiveView('planner')); }} style={{ color: '#64748b' }}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
            </IconButton>
          )}
          <TuneIcon style={{ fontSize: 20, color: '#3b82f6' }} />
          <Typography style={{ fontWeight: 800, fontSize: embedded ? '0.95rem' : '1.15rem', color: '#1e293b' }}>
            {isPt ? 'Optimização VRP' : 'VRP Optimization'}
          </Typography>
        </Box>
        <Box style={{ display: 'flex', gap: 8 }}>
          {canWrite && (
          <Button
            className={classes.optimizeBtn}
            startIcon={optimizing ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <PlayArrowIcon style={{ fontSize: 16 }} />}
            onClick={handleOptimize}
            disabled={optimizing || selectedOrders.length === 0 || selectedVehicles.length === 0}
          >
            {optimizing ? (isPt ? 'A optimizar...' : 'Optimizing...') : (isPt ? 'Optimizar' : 'Optimize')}
          </Button>
          )}
          {canWrite && currentPlan && (
            <Button
              className={classes.dispatchBtn}
              startIcon={dispatching ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 16 }} />}
              onClick={handleDispatch}
              disabled={dispatching || routes.length === 0}
            >
              {dispatching ? (isPt ? 'A despachar...' : 'Dispatching...') : (isPt ? 'Despachar Tudo' : 'Dispatch All')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats bar */}
      {currentPlan && (
        <Box className={classes.statsBar}>
          <Chip className={classes.statChip} size="small" label={`${routes.length} ${isPt ? 'rotas' : 'routes'}`} />
          <Chip className={classes.statChip} size="small" label={`${totalDeliveries} ${isPt ? 'entregas' : 'deliveries'}`} />
          <Chip className={classes.statChip} size="small" label={`${Math.round(totalDistanceKm)} km`} />
          <Chip className={classes.statChip} size="small" label={`${avgUtilization}% ${isPt ? 'ocupa\u00e7\u00e3o' : 'utilization'}`} />
          {unassigned.length > 0 && (
            <Chip
              size="small"
              label={`${unassigned.length} ${isPt ? 'n\u00e3o atribu\u00eddas' : 'unassigned'}`}
              style={{ fontWeight: 700, fontSize: '0.72rem', height: 26, background: '#fee2e2', color: '#dc2626' }}
            />
          )}
        </Box>
      )}

      {/* Error banner */}
      {vrpError && (
        <Box style={{ margin: '0 24px 8px', padding: '8px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <Typography style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>
            {vrpError}
          </Typography>
        </Box>
      )}

      {/* Body: Left | Center | Right */}
      <Box className={classes.body}>
        {/* LEFT PANEL */}
        <Box className={classes.leftPanel}>
          {loadingData ? (
            <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/* ── ORDERS CARD ── */}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader} onClick={() => toggleSection('orders')}>
                  <Box className={classes.sectionHeaderLeft}>
                    <Box className={classes.sectionIcon} style={{ background: '#eef2ff' }}>
                      <AssignmentIcon style={{ fontSize: 14, color: '#3b82f6' }} />
                    </Box>
                    <Box>
                      <Typography className={classes.sectionTitle}>{isPt ? 'Ordens Pendentes' : 'Pending Orders'}</Typography>
                    </Box>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Chip label={`${selectedOrders.length}/${orders.length}`} size="small"
                      className={classes.sectionCount}
                      style={{ background: selectedOrders.length > 0 ? '#3b82f6' : '#e2e8f0', color: selectedOrders.length > 0 ? '#fff' : '#64748b' }} />
                    {openSections.orders ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                  </Box>
                </Box>
                <Collapse in={openSections.orders}>
                  <Box className={classes.sectionBody}>
                    <Box className={classes.selectAll} onClick={toggleAllOrders} style={{ padding: '4px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      <Checkbox size="small" checked={selectedOrders.length === orders.length && orders.length > 0}
                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < orders.length} style={{ padding: 2 }} />
                      <span style={{ fontSize: '0.6rem', color: '#64748b' }}>{isPt ? 'Seleccionar tudo' : 'Select all'}</span>
                    </Box>
                    <Box className={classes.listWrap}>
                      {orders.map(order => {
                        const oid = order.id || order._id;
                        return (
                          <Box key={oid} className={classes.listItem} onClick={() => toggleOrder(oid)}>
                            <Checkbox size="small" checked={selectedOrders.includes(oid)} style={{ padding: 2 }} />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Box style={{ fontWeight: 600, fontSize: '0.7rem' }}>{order.orderNumber || `OT-${oid}`}</Box>
                              <Box className={classes.listItemSub}>
                                {order.pickup?.locationName || order.pickup?.address || '?'} → {order.delivery?.locationName || order.delivery?.address || '?'}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                      {orders.length === 0 && (
                        <Typography style={{ padding: '12px', fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center' }}>
                          {isPt ? 'Nenhuma ordem pendente' : 'No pending orders'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* ── VEHICLES CARD ── */}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader} onClick={() => toggleSection('vehicles')}>
                  <Box className={classes.sectionHeaderLeft}>
                    <Box className={classes.sectionIcon} style={{ background: '#f0fdf4' }}>
                      <LocalShippingIcon style={{ fontSize: 14, color: '#22c55e' }} />
                    </Box>
                    <Typography className={classes.sectionTitle}>{isPt ? 'Veículos' : 'Vehicles'}</Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Chip label={`${selectedVehicles.length}/${vehicles.length}`} size="small"
                      className={classes.sectionCount}
                      style={{ background: selectedVehicles.length > 0 ? '#22c55e' : '#e2e8f0', color: selectedVehicles.length > 0 ? '#fff' : '#64748b' }} />
                    {openSections.vehicles ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                  </Box>
                </Box>
                <Collapse in={openSections.vehicles}>
                  <Box className={classes.sectionBody}>
                    <Box className={classes.selectAll} onClick={toggleAllVehicles} style={{ padding: '4px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      <Checkbox size="small" checked={selectedVehicles.length === vehicles.length && vehicles.length > 0}
                        indeterminate={selectedVehicles.length > 0 && selectedVehicles.length < vehicles.length} style={{ padding: 2 }} />
                      <span style={{ fontSize: '0.6rem', color: '#64748b' }}>{isPt ? 'Seleccionar tudo' : 'Select all'}</span>
                    </Box>
                    <Box className={classes.listWrap}>
                      {vehicles.map(v => {
                        const vid = v.id || v._id;
                        return (
                          <Box key={vid} className={classes.listItem} onClick={() => toggleVehicle(vid)}>
                            <Checkbox size="small" checked={selectedVehicles.includes(vid)} style={{ padding: 2 }} />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Box style={{ fontWeight: 600, fontSize: '0.7rem' }}>{v.plate}</Box>
                              <Box className={classes.listItemSub}>
                                {v.type || ''}{v.maxWeightKg ? ` — ${v.maxWeightKg}kg` : v.capacity ? ` — ${v.capacity}` : ''}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* ── DRIVERS CARD ── */}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader} onClick={() => toggleSection('drivers')}>
                  <Box className={classes.sectionHeaderLeft}>
                    <Box className={classes.sectionIcon} style={{ background: '#fef3c7' }}>
                      <PersonIcon style={{ fontSize: 14, color: '#f59e0b' }} />
                    </Box>
                    <Typography className={classes.sectionTitle}>{isPt ? 'Motoristas' : 'Drivers'}</Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Chip label={`${selectedDrivers.length}/${drivers.length}`} size="small"
                      className={classes.sectionCount}
                      style={{ background: selectedDrivers.length > 0 ? '#f59e0b' : '#e2e8f0', color: selectedDrivers.length > 0 ? '#fff' : '#64748b' }} />
                    {openSections.drivers ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                  </Box>
                </Box>
                <Collapse in={openSections.drivers}>
                  <Box className={classes.sectionBody}>
                    <Box className={classes.selectAll} onClick={toggleAllDrivers} style={{ padding: '4px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      <Checkbox size="small" checked={selectedDrivers.length === drivers.length && drivers.length > 0}
                        indeterminate={selectedDrivers.length > 0 && selectedDrivers.length < drivers.length} style={{ padding: 2 }} />
                      <span style={{ fontSize: '0.6rem', color: '#64748b' }}>{isPt ? 'Seleccionar tudo' : 'Select all'}</span>
                    </Box>
                    <Box className={classes.listWrap}>
                      {drivers.map(d => {
                        const did = d.id || d._id;
                        return (
                          <Box key={did} className={classes.listItem} onClick={() => toggleDriver(did)}>
                            <Checkbox size="small" checked={selectedDrivers.includes(did)} style={{ padding: 2 }} />
                            <Box style={{ fontWeight: 600, fontSize: '0.7rem' }}>{d.name}</Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* ── DEPOT CARD ── */}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader} onClick={() => toggleSection('depot')}>
                  <Box className={classes.sectionHeaderLeft}>
                    <Box className={classes.sectionIcon} style={{ background: '#eef2ff' }}>
                      <HomeWorkIcon style={{ fontSize: 14, color: '#6366f1' }} />
                    </Box>
                    <Typography className={classes.sectionTitle}>{isPt ? 'Armazém' : 'Warehouse'}</Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {depotId && <CheckCircleIcon style={{ fontSize: 14, color: '#22c55e' }} />}
                    {openSections.depot ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                  </Box>
                </Box>
                <Collapse in={openSections.depot}>
                  <Box className={classes.sectionBody}>
                    <Box className={classes.configSection}>
                      <FormControl variant="outlined" size="small" fullWidth className={classes.configField}>
                        <Select value={depotId} onChange={(e) => setDepotId(e.target.value)} displayEmpty>
                          <MenuItem value=""><em style={{ color: '#94a3b8' }}>{isPt ? 'Seleccionar armazém...' : 'Select warehouse...'}</em></MenuItem>
                          {warehouses.map(w => (
                            <MenuItem key={w.id || w._id} value={w.id || w._id} style={{ fontSize: '0.72rem' }}>
                              {w.name || w.address || `Armazém #${w.id || w._id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* ── CONFIG CARD ── */}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader} onClick={() => toggleSection('config')}>
                  <Box className={classes.sectionHeaderLeft}>
                    <Box className={classes.sectionIcon} style={{ background: '#f1f5f9' }}>
                      <TuneIcon style={{ fontSize: 14, color: '#64748b' }} />
                    </Box>
                    <Typography className={classes.sectionTitle}>{isPt ? 'Configurações' : 'Settings'}</Typography>
                  </Box>
                  {openSections.config ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                </Box>
                <Collapse in={openSections.config}>
                  <Box className={classes.sectionBody}>
                    <Box className={classes.configSection}>
                      <Box>
                        <Typography className={classes.configLabel}>{isPt ? 'Max paragens por rota' : 'Max stops per route'}</Typography>
                        <TextField className={classes.configField} variant="outlined" size="small" fullWidth type="number"
                          placeholder="10" value={maxStops}
                          onChange={(e) => setMaxStops(Math.max(1, parseInt(e.target.value) || 1))}
                          inputProps={{ min: 1, max: 50 }} />
                      </Box>
                      <Box>
                        <Typography className={classes.configLabel}>{isPt ? 'Max tempo rota (min)' : 'Max route time (min)'}</Typography>
                        <TextField className={classes.configField} variant="outlined" size="small" fullWidth type="number"
                          placeholder="600" value={maxTime}
                          onChange={(e) => setMaxTime(Math.max(1, parseInt(e.target.value) || 1))}
                          inputProps={{ min: 1 }} />
                      </Box>
                      <Box>
                        <Typography className={classes.configLabel}>{isPt ? 'Optimizar por' : 'Optimize by'}</Typography>
                        <FormControl variant="outlined" size="small" fullWidth className={classes.configField}>
                          <Select value={optimizeBy} onChange={(e) => setOptimizeBy(e.target.value)} displayEmpty>
                            <MenuItem value="distance">{isPt ? 'Distância' : 'Distance'}</MenuItem>
                            <MenuItem value="time">{isPt ? 'Tempo' : 'Time'}</MenuItem>
                            <MenuItem value="cost">{isPt ? 'Custo' : 'Cost'}</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </Box>

        {/* CENTER AREA */}
        <Box className={classes.centerArea}>
          {!currentPlan ? (
            <Box className={classes.emptyState}>
              <TuneIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
              <Typography style={{ fontSize: '1rem', fontWeight: 600 }}>
                {isPt ? 'Planeamento Autom\u00e1tico de Rotas' : 'Automatic Route Planning'}
              </Typography>
              <Typography style={{ fontSize: '0.82rem', maxWidth: 340, textAlign: 'center' }}>
                {isPt
                  ? 'Seleccione ordens de transporte e ve\u00edculos no painel esquerdo, depois clique "Optimizar" para gerar rotas automaticamente.'
                  : 'Select transport orders and vehicles on the left panel, then click "Optimize" to generate routes automatically.'}
              </Typography>
              {selectedOrders.length > 0 && selectedVehicles.length > 0 && (
                <Chip
                  size="small"
                  icon={<CheckCircleIcon style={{ fontSize: 14, color: '#22c55e' }} />}
                  label={`${selectedOrders.length} OTs + ${selectedVehicles.length} ${isPt ? 've\u00edculos' : 'vehicles'} ${isPt ? 'prontos' : 'ready'}`}
                  style={{ fontWeight: 600, fontSize: '0.72rem', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}
                />
              )}
            </Box>
          ) : (
            <Box style={{ width: '100%', maxWidth: 600 }}>
              {/* Summary cards */}
              <Box className={classes.summaryCards}>
                <Box className={classes.summaryCard}>
                  <Typography className={classes.summaryValue}>{routes.length}</Typography>
                  <Typography className={classes.summaryLabel}>{isPt ? 'Rotas' : 'Routes'}</Typography>
                </Box>
                <Box className={classes.summaryCard}>
                  <Typography className={classes.summaryValue}>{totalDeliveries}</Typography>
                  <Typography className={classes.summaryLabel}>{isPt ? 'Entregas' : 'Deliveries'}</Typography>
                </Box>
                <Box className={classes.summaryCard}>
                  <Typography className={classes.summaryValue}>{Math.round(totalDistanceKm)}</Typography>
                  <Typography className={classes.summaryLabel}>km</Typography>
                </Box>
                <Box className={classes.summaryCard}>
                  <Typography className={classes.summaryValue}>{avgUtilization}%</Typography>
                  <Typography className={classes.summaryLabel}>{isPt ? 'Ocupa\u00e7\u00e3o' : 'Utilization'}</Typography>
                </Box>
              </Box>

              {/* Plan status */}
              {currentPlan.status && (
                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Chip
                    size="small"
                    label={currentPlan.status === 'dispatched'
                      ? (isPt ? 'Despachado' : 'Dispatched')
                      : currentPlan.status === 'optimized'
                        ? (isPt ? 'Optimizado' : 'Optimized')
                        : currentPlan.status}
                    style={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      background: currentPlan.status === 'dispatched' ? '#dcfce7' : '#dbeafe',
                      color: currentPlan.status === 'dispatched' ? '#16a34a' : '#2563eb',
                    }}
                  />
                </Box>
              )}

              {/* Route list (center) */}
              {routes.map((route, idx) => {
                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                return (
                  <Box key={idx} style={{
                    margin: '8px 0', padding: '12px 16px', background: '#fff',
                    borderRadius: 10, border: `1px solid ${color}22`,
                    borderLeft: `4px solid ${color}`,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  }}>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                        {isPt ? 'Rota' : 'Route'} {idx + 1}
                        {route.vehiclePlate && ` \u2014 ${route.vehiclePlate}`}
                      </Typography>
                      <Box style={{ display: 'flex', gap: 6 }}>
                        <Chip size="small" label={`${route.stops?.length || 0} ${isPt ? 'paragens' : 'stops'}`}
                          style={{ height: 20, fontSize: '0.62rem', fontWeight: 600 }} />
                        <Chip size="small" label={`${Math.round(route.distanceKm || route.distance || 0)} km`}
                          style={{ height: 20, fontSize: '0.62rem', fontWeight: 600 }} />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* RIGHT PANEL — only when plan exists */}
        {currentPlan && (
          <Box className={classes.rightPanel}>
            <Typography className={classes.sectionTitle}>
              {isPt ? 'Rotas Geradas' : 'Generated Routes'}
            </Typography>

            {routes.map((route, idx) => {
              const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
              const expanded = expandedRoutes[idx];
              const utilPct = route.utilization || route.utilizationPct || 0;
              return (
                <Box key={idx} className={classes.routeCard} style={{ borderColor: `${color}44` }}>
                  <Box className={classes.routeCardHeader} onClick={() => toggleRouteExpand(idx)}>
                    <Box className={classes.routeCardInfo}>
                      <Box style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <Box>
                        <Typography style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                          {route.vehiclePlate || `${isPt ? 'Ve\u00edculo' : 'Vehicle'} ${idx + 1}`}
                        </Typography>
                        {route.driverName && (
                          <Typography style={{ fontSize: '0.68rem', color: '#64748b' }}>
                            {route.driverName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton size="small">
                      {expanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
                    </IconButton>
                  </Box>

                  {/* Meta chips */}
                  <Box className={classes.routeCardMeta}>
                    <Chip className={classes.routeCardMetaChip} size="small"
                      label={`${route.stops?.length || 0} ${isPt ? 'paragens' : 'stops'}`} />
                    <Chip className={classes.routeCardMetaChip} size="small"
                      label={`${Math.round(route.distanceKm || route.distance || 0)} km`} />
                    <Chip className={classes.routeCardMetaChip} size="small"
                      label={`${Math.round(route.durationMin || (route.duration || 0) / 60)} min`} />
                  </Box>

                  {/* Utilization bar */}
                  <Box style={{ padding: '0 12px 2px' }}>
                    <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>
                      {isPt ? 'Ocupa\u00e7\u00e3o' : 'Utilization'}: {Math.round(utilPct)}%
                    </Typography>
                  </Box>
                  <Box className={classes.utilizationBar}>
                    <Box
                      className={classes.utilizationFill}
                      style={{
                        width: `${Math.min(100, utilPct)}%`,
                        background: utilPct > 90 ? '#ef4444' : utilPct > 70 ? '#f59e0b' : '#22c55e',
                      }}
                    />
                  </Box>

                  {/* Stops list (collapsible) */}
                  <Collapse in={expanded}>
                    <Box style={{ paddingBottom: 8 }}>
                      {(route.stops || []).map((stop, si) => (
                        <Box key={si} className={classes.stopItem}>
                          <Box className={classes.stopNumber} style={{ background: color }}>
                            {si + 1}
                          </Box>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}>
                              {stop.locationName || stop.name || stop.address || `${isPt ? 'Paragem' : 'Stop'} ${si + 1}`}
                            </Typography>
                            {stop.arrivalTime && (
                              <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                                {stop.arrivalTime}
                              </Typography>
                            )}
                          </Box>
                          {stop.orderNumber && (
                            <Chip size="small" label={stop.orderNumber}
                              style={{ height: 18, fontSize: '0.58rem', fontWeight: 600, background: '#eff6ff', color: '#3b82f6' }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {/* Unassigned orders */}
            {unassigned.length > 0 && (
              <Box className={classes.unassignedSection}>
                <Box className={classes.unassignedHeader}>
                  <WarningIcon style={{ fontSize: 16 }} />
                  {isPt ? 'Ordens N\u00e3o Atribu\u00eddas' : 'Unassigned Orders'} ({unassigned.length})
                </Box>
                {unassigned.map((item, ui) => (
                  <Box key={ui} className={classes.unassignedItem}>
                    <Typography style={{ fontWeight: 600, fontSize: '0.72rem' }}>
                      {item.orderNumber || `OT #${item.orderId || ui + 1}`}
                    </Typography>
                    {item.reason && (
                      <Typography style={{ fontSize: '0.62rem', color: '#b91c1c', fontStyle: 'italic' }}>
                        {item.reason}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default VRPView;
